import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { FOLLOWUP_TYPES, FOLLOWUP_PRIORITIES } from "@/config/crm";

export function FollowUpDialog({ open, onOpenChange, followup, preset = {}, owners = [], onSaved }) {
  const isEdit = Boolean(followup);
  const [type, setType] = useState("General Follow-up");
  const [priority, setPriority] = useState("medium");
  const [dueAt, setDueAt] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (followup) {
      setType(followup.type); setPriority(followup.priority);
      setDueAt(followup.due_at ? followup.due_at.slice(0, 16) : "");
      setOwnerId(followup.owner_id || ""); setNotes(followup.notes || "");
    } else {
      setType("General Follow-up"); setPriority("medium"); setNotes(""); setOwnerId("");
      const d = new Date(Date.now() + 24 * 3600 * 1000);
      setDueAt(new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16));
    }
  }, [open, followup]);

  const submit = async () => {
    if (!dueAt) { toast.error("Please choose a due date/time."); return; }
    setSaving(true);
    const payload = {
      type, priority, due_at: new Date(dueAt).toISOString(),
      owner_id: ownerId || null, notes: notes || null,
      ...(isEdit ? {} : { lead_id: preset.lead_id || null, patient_id: preset.patient_id || null, appointment_id: preset.appointment_id || null }),
    };
    try {
      if (isEdit) await api.put(`/followups/${followup.id}`, payload);
      else await api.post("/followups", payload);
      toast.success(isEdit ? "Follow-up updated" : "Follow-up created");
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="followup-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Edit Follow-up" : "New Follow-up"}</DialogTitle>
          <DialogDescription>Schedule a task linked to this lead or patient.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Type</Label>
              <Select value={type} onValueChange={setType}><SelectTrigger data-testid="fu-type"><SelectValue /></SelectTrigger>
                <SelectContent>{FOLLOWUP_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="space-y-1.5"><Label>Priority</Label>
              <Select value={priority} onValueChange={setPriority}><SelectTrigger data-testid="fu-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{FOLLOWUP_PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent></Select>
            </div>
          </div>
          <div className="space-y-1.5"><Label>Due date & time</Label>
            <Input type="datetime-local" value={dueAt} onChange={(e) => setDueAt(e.target.value)} data-testid="fu-due" />
          </div>
          <div className="space-y-1.5"><Label>Assign owner</Label>
            <Select value={ownerId || undefined} onValueChange={setOwnerId}><SelectTrigger data-testid="fu-owner"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>{owners.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="space-y-1.5"><Label>Notes</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="fu-notes" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="fu-submit">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}{isEdit ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
